import type { JSX } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AvatarFrame } from '../../shared/components/AvatarFrame.js';
import type { EliminatedReveal } from '../types.js';
import './CastGrid.css'; // the shared [data-eliminated] Oxblood name-plate rule
import './RevealPlate.css';

interface RevealPlateProps {
  /** The vote-eliminated player — the face-up figure + the reveal line. */
  eliminated: EliminatedReveal;
  /** AFK non-voters eliminated in the same resolution — text-only list. */
  alsoEliminated: readonly EliminatedReveal[];
}

/**
 * The RoleReveal recto: a carte-de-visite turned face-up. The eliminated
 * portrait flips up (Oxblood name-plate = the consequence accent), then the
 * line names them and their now-public role. A Ghost adds the Spectral
 * manifestation halo + role-coloured text (the otherworldly accent) — both
 * accents on screen, never on the same element (Two-Accent Rule).
 *
 * AFK non-voters eliminated in the same resolution are listed beneath as text
 * only ("{Name} — {Role}"), no portrait.
 */
export function RevealPlate({ eliminated, alsoEliminated }: RevealPlateProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="susdle-reveal-plate" data-role={eliminated.role}>
      <div className="susdle-reveal-plate__flip" aria-hidden>
        <div className="susdle-reveal-plate__flip-inner">
          <div className="susdle-reveal-plate__flip-front" data-eliminated="yes">
            <AvatarFrame
              player={eliminated.player}
              isHost={false}
              motionEnabled={false}
              suppressEntry
              suppressFloat
            />
          </div>
        </div>
      </div>

      <p className="susdle-reveal-plate__line">
        <Trans
          i18nKey="reveal.roleReveal.eliminated"
          values={{ name: eliminated.player.name, role: t(`reveal.roles.${eliminated.role}`) }}
          components={{ role: <span className="susdle-reveal-plate__role" /> }}
        />
      </p>

      {alsoEliminated.length > 0 ? (
        <div className="susdle-reveal-plate__also">
          <span className="susdle-reveal-plate__also-label">
            {t('reveal.roleReveal.alsoEliminated.label')}
          </span>
          <div className="susdle-reveal-plate__also-row">
            {alsoEliminated.map((entry) => (
              <span key={entry.player.id} className="susdle-reveal-plate__also-item">
                <Trans
                  i18nKey="reveal.roleReveal.alsoEliminated.item"
                  values={{ name: entry.player.name, role: t(`reveal.roles.${entry.role}`) }}
                  components={{ role: <b /> }}
                />
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
